/**
 * Persönliches Notizsystem ("digitales Klemmbrett"). Jeder Benutzer sieht
 * und verwaltet ausschließlich seine eigenen Notizen - die Isolation wird
 * serverseitig erzwungen (siehe backend/src/routes/notes.ts), diese Seite
 * zeigt daher schlicht alles, was die API für den eingeloggten Benutzer liefert.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { NoteForm } from "./NoteForm";
import { parseServerDate } from "../../utils/formatDate";
import type { Note } from "../../types";

function formatDate(iso: string): string {
  return parseServerDate(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Notes() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Note | null | undefined>(undefined);

  const load = useCallback(() => {
    setLoading(true);
    return api.get<Note[]>("/api/notes").then(setNotes).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(note: Note) {
    if (!confirm(`Notiz "${note.title || "Ohne Titel"}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/notes/${note.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">📌 Notizen</h1>
          <p className="text-sm text-gray-500 mt-1">Dein persönliches Klemmbrett — nur für dich sichtbar.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing(null)}>+ Neue Notiz</button>
      </div>

      {loading ? (
        <Spinner />
      ) : notes.length === 0 ? (
        <Card className="py-10">
          <EmptyState message="Noch keine Notizen vorhanden. Erstelle deine erste Notiz." />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {notes.map((note) => (
            <Card key={note.id} className="flex flex-col border-l-2 border-l-accent">
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-gray-100 uppercase tracking-wide text-sm truncate">
                  {note.title || "Ohne Titel"}
                </h3>
                <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap break-words line-clamp-6">
                  {note.content}
                </p>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-border">
                <span className="text-xs text-gray-500">Geändert: {formatDate(note.updated_at)}</span>
                <div className="flex gap-3 shrink-0">
                  <button className="text-accent text-xs hover:underline" onClick={() => setEditing(note)}>Bearbeiten</button>
                  <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(note)}>Löschen</button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing !== undefined && (
        <NoteForm
          note={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </div>
  );
}
